import './ExportedOpportunities.css';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineChevronLeft, HiOutlineChevronRight, HiOutlineMagnifyingGlass, HiOutlineLink, HiOutlineInformationCircle } from 'react-icons/hi2';
import CustomDropdownWithSearch from '../../components/CustomDropdownWithSearch/CustomDropdownWithSearch';
import Spinner from '../../components/Spinner/Spinner';
import { getCrmOpportunities, CrmOpportunityRecord, PaginationInfo } from '../../services/crmService';
import { createExportedOpportunity, fetchExportedOpportunityIds } from '../../services/exportedOpportunitiesService';
import { fetchAllRetailersWithChannels } from '../../services/mapPageService';
import { trackCrmEvent } from '../../utils/posthogUtils';

function ExportedOpportunities() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [opportunities, setOpportunities] = useState<CrmOpportunityRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creatingPitchId, setCreatingPitchId] = useState<string | null>(null);
  const [existingPitchIds, setExistingPitchIds] = useState<Set<string>>(new Set());

  const [ultimateParentFilter, setUltimateParentFilter] = useState<string[]>([]);
  const [ultimateParentOptions, setUltimateParentOptions] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1, limit: 50, totalCount: 0, totalPages: 1, hasNextPage: false, hasPreviousPage: false,
  });

  // Fetch ultimate parent options on mount
  useEffect(() => {
    fetchAllRetailersWithChannels().then((retailers) => {
      const names = retailers.map(r => r.ultimate_parent).sort((a, b) => a.localeCompare(b));
      setUltimateParentOptions(names);
    });
  }, []);

  const fetchData = async (page: number = 1, ultimateParent?: string) => {
    setLoading(true);
    setError('');

    const to = new Date();
    const from = new Date('2026-04-15');

    const fromStr = from.toISOString().split('T')[0];
    const toStr = to.toISOString().split('T')[0];

    const [result, pitchIds] = await Promise.all([
      getCrmOpportunities(fromStr, toStr, page, ultimateParent),
      fetchExportedOpportunityIds(),
    ]);

    if (result.success) {
      setOpportunities(result.opportunities);
      setPagination(result.pagination);
    } else {
      setError(result.message);
    }
    setExistingPitchIds(pitchIds);
    setLoading(false);
  };

  const selectedUltimateParent = ultimateParentFilter.length > 0 ? ultimateParentFilter[0] : undefined;

  useEffect(() => {
    fetchData(currentPage, selectedUltimateParent);
  }, [currentPage, selectedUltimateParent]);

  const filteredData = useMemo(() => {
    return opportunities
      .filter((item) => {
        if (!item.OpportunityStores || item.OpportunityStores.length === 0) return false;
        const searchMatch = searchQuery === '' ||
          (item.Topic || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
          (item.PotentialCustomer || '').toLowerCase().includes(searchQuery.toLowerCase());
        return searchMatch;
      })
      .sort((a, b) => new Date(b.CreatedOn).getTime() - new Date(a.CreatedOn).getTime());
  }, [opportunities, searchQuery]);

  const handlePreviousPage = () => {
    if (pagination.hasPreviousPage) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.hasNextPage) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatRevenue = (revenue: number): string => {
    if (!revenue) return '$0';
    return `$${revenue.toLocaleString()}`;
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'Meeting Fixed':
        return 'status-badge status-meeting-fixed';
      case 'Mail Sent':
        return 'status-badge status-mail-sent';
      case 'Opportunity Created':
        return 'status-badge status-opportunity-created';
      case 'Draft':
        return 'status-badge status-draft';
      case 'No Pitch':
        return 'status-badge status-no-pitch';
      default:
        return 'status-badge';
    }
  };

  const getActionButtonClass = (status: string): string => {
    switch (status) {
      case 'No Pitch':
        return 'action-btn action-create-pitch';
      case 'Sent':
        return 'action-btn action-view-pitch';
      case 'Draft':
        return 'action-btn action-edit-draft';
      default:
        return 'action-btn';
    }
  };

  // TODO: Remove hardcoded POI IDs once CRM API sends poi_id in OpportunityStores
  const TEMP_POI_IDS = [
    'zzw-223@5py-dhc-c3q',
    '223-222@8fy-8gv-tgk',
    'zzw-223@62j-rtf-6x5',
    '223-222@8dg-gtp-q9f',
    '222-222@5z4-sds-q2k',
    'zzw-222@8sz-ty3-whq',
    '222-222@5pz-w9c-w6k',
    '222-222@8gk-tqn-y5f',
    '224-222@5pr-msp-nkf',
    '223-222@63s-ypf-f2k',
  ];

  const handleCreatePitch = async (row: CrmOpportunityRecord) => {
    setCreatingPitchId(row.OpportunityId);
    const poiIds = (row.OpportunityStores || [])
      .map((s) => s.storeNumber)
      .filter(Boolean);

    const result = await createExportedOpportunity(row.OpportunityId, poiIds.length > 0 ? poiIds : TEMP_POI_IDS, null);

    setCreatingPitchId(null);

    if (result.success) {
      trackCrmEvent.opportunityCreated(row.PotentialCustomer || row.Topic, row.EstRevenue, row.OpportunityStores?.length || 0);
      setExistingPitchIds((prev) => new Set(prev).add(row.OpportunityId));
      navigate(`/dashboard/viewpitch/${row.OpportunityId}`);
    } else {
      trackCrmEvent.opportunityCreateFailed(row.PotentialCustomer || row.Topic, result.message);
      alert(result.message);
    }
  };

  if (loading) {
    return (
      <div className="exported-opportunities">
        <div className="exported-opp-loader">
          <Spinner size="large" />
          <p className="exported-opp-loader-text">Loading exported opportunities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="exported-opportunities">
      <div className="exported-opp-beta-banner">
        This is in a beta version. If you have any feedback please contact{' '}
        <a href="mailto:jpande@gatekeepersystems.com">jpande@gatekeepersystems.com</a>
      </div>
      <div className="crm-sync-notice">
        <HiOutlineInformationCircle className="crm-sync-notice-icon" />
        Currently syncing to test environment. Data will not appear in the live CRM during this testing phase.
      </div>

      <div className="export-header">
        <div className="export-top-row">
          <div className="export-search-bar">
            <HiOutlineMagnifyingGlass className="export-search-icon" />
            <input
              type="text"
              placeholder="Search by Retailer / Brand / Proposed Solution"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              className="export-search-input"
            />
          </div>

          <div className="export-dropdowns">
            <CustomDropdownWithSearch
              value={ultimateParentFilter}
              options={ultimateParentOptions}
              onChange={(val) => { setUltimateParentFilter(val); setCurrentPage(1); }}
              placeholder="All Retailers"
              singleSelect
              showTags={false}
            />
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="exported-opp-table-wrapper">
        <table className="exported-opp-table">
          <thead>
            <tr>
              <th>Brand</th>
              <th>Topic</th>
              <th>CRM</th>
              <th>CRM Export Date</th>
              <th>Current Status</th>
              <th>Number of Sites</th>
              <th>Estimated Revenue</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {error ? (
              <tr>
                <td colSpan={8} className="empty-state">
                  {error}
                </td>
              </tr>
            ) : filteredData.length > 0 ? (
              filteredData.map((row: CrmOpportunityRecord) => (
                <tr key={row.OpportunityId}>
                  <td className="export-brand-cell">{row.PotentialCustomer || selectedUltimateParent || '-'}</td>
                  <td className="export-topic-cell">{row.Topic || '-'}</td>
                  <td>
                    <a
                      href={`https://gkuat.crm.dynamics.com/main.aspx?appid=0a5f5ee2-2f0e-e811-a951-000d3a161343&pagetype=entityrecord&etn=opportunity&id=${row.OpportunityId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="crm-details-btn"
                    >
                      Open in CRM <HiOutlineLink />
                    </a>
                  </td>
                  <td>{formatDate(row.CreatedOn)}</td>
                  <td>
                    <span className={getStatusBadgeClass('Opportunity Created')}>Opportunity Created</span>
                  </td>
                  <td className="text-center">{row.OpportunityStores?.length || 0}</td>
                  <td className="text-center">{formatRevenue(row.EstRevenue)}</td>
                  <td>
                    <div className="export-actions-cell">
                      <button
                        className={getActionButtonClass('Sent')}
                        disabled={creatingPitchId === row.OpportunityId}
                        onClick={() => existingPitchIds.has(row.OpportunityId) ? navigate(`/dashboard/viewpitch/${row.OpportunityId}`) : handleCreatePitch(row)}
                      >
                        {creatingPitchId === row.OpportunityId ? 'Generating...' : 'View Opportunity Map'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="empty-state">
                  No opportunities match the selected filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="exported-opp-pagination">
          <button
            className="pagination-btn"
            onClick={handlePreviousPage}
            disabled={!pagination.hasPreviousPage}
            title="Previous Page"
          >
            <HiOutlineChevronLeft />
          </button>
          <span className="pagination-info">
            Page {pagination.page} of {pagination.totalPages} ({pagination.totalCount} total)
          </span>
          <button
            className="pagination-btn"
            onClick={handleNextPage}
            disabled={!pagination.hasNextPage}
            title="Next Page"
          >
            <HiOutlineChevronRight />
          </button>
        </div>
      )}

    </div>
  );
}

export default ExportedOpportunities;
